
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, SaveIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { OutfitLayer } from '../types';
import { generateOutfitMetadata } from '../services/geminiService';
import { SparklesIcon } from './icons';

interface OutfitNameInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (outfitName: string, description: string, tags: string[], saveAsNew: boolean, poseNote: string) => void;
  isLoading: boolean;
  initialName?: string;
  initialDescription?: string;
  initialTags?: string[];
  previewImageUrl?: string | null;
  poseDescription?: string;
  backgroundDescription?: string;
  initialPoseNote?: string;
  currentLayer?: OutfitLayer;
}

const OutfitNameInputModal: React.FC<OutfitNameInputModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading,
  initialName = '',
  initialDescription = '',
  initialTags = [],
  previewImageUrl,
  poseDescription,
  backgroundDescription,
  initialPoseNote = '',
  currentLayer
}) => {
  const { t } = useTranslation();
  const [outfitName, setOutfitName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [tags, setTags] = useState(initialTags.join(', '));
  const [saveAsNew, setSaveAsNew] = useState(false);
  const [poseNote, setPoseNote] = useState(initialPoseNote);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSmartLabel = async () => {
    if (!currentLayer) return;
    setIsGeneratingMetadata(true);
    try {
      const metadata = await generateOutfitMetadata(description, currentLayer, t('i18n_lang_code') || 'en', poseDescription);
      if (metadata.title) setOutfitName(metadata.title);
      if (metadata.description) setDescription(metadata.description);
    } catch (error) {
      console.error("Failed to generate metadata", error);
    } finally {
      setIsGeneratingMetadata(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setOutfitName(initialName);
      setDescription(initialDescription);
      setTags(initialTags.join(', '));
      setSaveAsNew(false);
      setPoseNote(initialPoseNote);
      // Automatically focus the input when the modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialName, initialDescription, initialTags, initialPoseNote]);

  // Effect to append pose and background description when "Save as New" is checked
  useEffect(() => {
    if (saveAsNew) {
      let nameSuffix = '';
      let descAppend = '';

      if (poseDescription) {
        // More sophisticated suffix generation
        const shortPose = poseDescription.split(',')[0].replace('pose_label_', '').split('_').slice(0, 2).join(' ');
        nameSuffix += ` (${shortPose})`;
        descAppend += `\nPose: ${poseDescription}`;
      }

      if (backgroundDescription && backgroundDescription !== 'neutral light gray studio backdrop') {
        const shortBg = backgroundDescription.split(' ').slice(0, 2).join(' ');
        nameSuffix += ` @ ${shortBg}`;
        descAppend += `\nBackground: ${backgroundDescription}`;
      }

      if (initialName && !outfitName.includes(nameSuffix.trim()) && nameSuffix) {
        setOutfitName(`${initialName}${nameSuffix}`);
      }

      if (descAppend) {
        const trimmedAppend = descAppend.trim();
        if (!description.includes(trimmedAppend)) {
          setDescription((prev) => prev ? `${prev}\n\n${trimmedAppend}` : trimmedAppend);
        }
      }
    } else if (!saveAsNew && initialName) {
      // Revert if name still contains the suffix
      if (outfitName !== initialName && outfitName.startsWith(initialName)) {
        setOutfitName(initialName);
      }
    }
  }, [saveAsNew, poseDescription, backgroundDescription, initialName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (outfitName.trim() && !isLoading) {
      const tagArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      onSave(outfitName.trim(), description.trim(), tagArray, saveAsNew, poseNote.trim());
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md flex flex-col shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{t('outfitNameInputModal_title')}</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('outfitNameInputModal_cancel')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto custom-scrollbar p-6">
              {previewImageUrl && (
                <div className="mb-4 flex justify-center">
                  <div className="w-32 h-48 rounded-lg overflow-hidden shadow-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 relative">
                    <img src={previewImageUrl} alt={t('outfitNameInputModal_previewAlt')} className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              {(poseDescription || backgroundDescription) && (
                <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-500 dark:text-gray-400 space-y-2 border border-gray-100 dark:border-gray-600">
                  {poseDescription && (
                    <div>
                      <span className="font-semibold block mb-1 uppercase tracking-wide text-xs text-indigo-500 dark:text-indigo-400">{t('savedOutfit_posePrefix')}</span>
                      {poseDescription}
                    </div>
                  )}
                  {backgroundDescription && (
                    <div className={`${poseDescription ? 'border-t border-gray-200 dark:border-gray-600 pt-2 mt-2' : ''}`}>
                      <span className="font-semibold block mb-1 uppercase tracking-wide text-xs text-indigo-500 dark:text-indigo-400">Background:</span>
                      {backgroundDescription}
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <div className="flex justify-between items-center mb-1">
                    <label htmlFor="outfit-name-input" className="sr-only">{t('outfitNameInputModal_title')}</label>
                    {currentLayer && (
                      <button
                        type="button"
                        onClick={handleSmartLabel}
                        disabled={isGeneratingMetadata || isLoading}
                        className="absolute right-0 top-0 bottom-0 px-3 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-800 rounded-r-lg text-xs font-semibold flex items-center transition-colors border-l border-gray-300 dark:border-gray-600 z-10"
                        title="Generate AI Title"
                      >
                        <SparklesIcon className={`w-3 h-3 mr-1 ${isGeneratingMetadata ? 'animate-spin' : ''}`} />
                        {isGeneratingMetadata ? '...' : 'AI Label'}
                      </button>
                    )}
                  </div>
                  <input
                    id="outfit-name-input"
                    ref={inputRef}
                    type="text"
                    value={outfitName}
                    onChange={(e) => setOutfitName(e.target.value)}
                    placeholder={t('outfitNameInputModal_placeholder')}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                    disabled={isLoading}
                    aria-required="true"
                  />
                </div>
                <div>
                  <label htmlFor="outfit-description-input" className="sr-only">{t('outfitNameInputModal_descriptionLabel')}</label>
                  <textarea
                    id="outfit-description-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('outfitNameInputModal_descriptionPlaceholder')}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400 resize-y"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label htmlFor="outfit-tags-input" className="sr-only">{t('outfitNameInputModal_tagsLabel')}</label>
                  <input
                    id="outfit-tags-input"
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder={t('outfitNameInputModal_tagsPlaceholder')}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label htmlFor="outfit-pose-note-input" className="sr-only">{t('outfitNameInputModal_poseNoteLabel')}</label>
                  <input
                    id="outfit-pose-note-input"
                    type="text"
                    value={poseNote}
                    onChange={(e) => setPoseNote(e.target.value)}
                    placeholder={t('outfitNameInputModal_poseNotePlaceholder')}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                    disabled={isLoading}
                  />
                </div>

                {initialName && (
                  <div className="flex items-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                    <input
                      id="save-as-new-checkbox"
                      type="checkbox"
                      checked={saveAsNew}
                      onChange={(e) => setSaveAsNew(e.target.checked)}
                      className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                      disabled={isLoading}
                    />
                    <label htmlFor="save-as-new-checkbox" className="ml-3 block text-sm font-medium text-gray-800 dark:text-gray-200 cursor-pointer">
                      {t('outfitNameInputModal_saveAsNew')}
                      <span className="block text-xs text-gray-500 dark:text-gray-400 font-normal mt-0.5">Creates a new copy instead of updating the existing outfit.</span>
                    </label>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2 text-base font-semibold text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                    disabled={isLoading}
                  >
                    {t('outfitNameInputModal_cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex items-center justify-center bg-gray-900 text-white font-semibold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-600 dark:hover:bg-indigo-500"
                    disabled={isLoading || outfitName.trim() === ''}
                  >
                    <SaveIcon className="w-5 h-5 mr-2" />
                    {t('outfitNameInputModal_saveOutfit')}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OutfitNameInputModal;
